import React from 'react';
import cn from 'classnames';
import { Row } from 'antd';
import { useTranslation } from 'react-i18next';

import Dropdown from '../../common/Dropdown';
import viewSettingsActions from '../../../actions/viewSettingsActions';
import { VIEW_MODES, SETTINGS_KEYS } from '../../../configs/viewSettings';

import availableFieldTypes from '../helpers/availableFieldTypes';
import { isSelfLinkField } from '../helpers/links';

import styles from './config.less';

const getItems = (fields, types) =>
  fields
    ? fields
        .filter((f) => types.includes(f.get('type')))
        .map((f) => ({ key: f.get('id'), text: f.get('name') }))
        .toJS()
    : [];

const FieldRow = ({ label, option, types, allowEmpty, viewSettings, fields, catalogId, viewId, sectionStart }) => {
  const value = (viewSettings && viewSettings.getIn([option, SETTINGS_KEYS.FIELD_ID])) || null;
  const items = getItems(fields, types);
  const list = allowEmpty ? [{ key: null, text: '' }, ...items] : items;

  const onSelect = (item) => {
    const fieldId = item ? item.key : null;
    viewSettingsActions.setOption({
      catalogId,
      viewId,
      viewMode: VIEW_MODES.GANTT,
      option,
      value: fieldId ? { type: 'field', fieldId } : null,
    });
  };

  return (
    <Row className={cn(styles.controlRow, { [styles.sectionStart]: sectionStart })}>
      <label className={styles.controlLabel}>{label}</label>
      <Dropdown
        disabled={!items.length}
        items={list}
        value={value}
        autocomplete
        withButton
        multiselect={false}
        onSelectItems={([item]) => onSelect(item)}
      />
    </Row>
  );
};

const GanttConfigOverlay = (props) => {
  const { t } = useTranslation();
  const { fields, catalogId, viewId, viewSettings } = props;
  const linkFields = fields?.filter((field) => isSelfLinkField(field, catalogId));
  const selectedLinkId = viewSettings?.getIn([SETTINGS_KEYS.GANTT_LINK_FIELD, SETTINGS_KEYS.FIELD_ID]);
  const hasLinkField = linkFields?.some((field) => field.get('id') === selectedLinkId);

  const slots = [
    { slot: 'start', option: SETTINGS_KEYS.GANTT_START },
    { slot: 'end', option: SETTINGS_KEYS.GANTT_END, allowEmpty: true },
    { slot: 'title', option: SETTINGS_KEYS.GANTT_TITLE, allowEmpty: true },
    { slot: 'progress', option: SETTINGS_KEYS.GANTT_PROGRESS, allowEmpty: true },
    { slot: 'color', option: SETTINGS_KEYS.COLOR, allowEmpty: true },
    { slot: 'employee', option: SETTINGS_KEYS.GANTT_EMPLOYEE, allowEmpty: true },
    { slot: 'link', option: SETTINGS_KEYS.GANTT_LINK_FIELD, allowEmpty: true },
  ];

  return (
    <div className={cn('ant-dropdown-menu ant-dropdown-menu-vertical', styles.overlay)}>
      {slots
        .filter(({ slot }) => slot !== 'link' || linkFields?.size)
        .map(({ slot, option, allowEmpty }) => (
          <FieldRow
            key={slot}
            label={t(`gantt.config.${slot}`)}
            option={option}
            types={availableFieldTypes[slot]}
            allowEmpty={allowEmpty}
            {...props}
            fields={slot === 'link' ? linkFields : fields}
            sectionStart={slot === 'employee'}
          />
        ))}
      {hasLinkField && (
        <Row className={styles.controlRow}>
          <label className={styles.controlLabel}>{t('gantt.config.linkDirection')}</label>
          <Dropdown
            items={['predecessors', 'successors'].map((key) => ({ key, text: t(`gantt.linkDirections.${key}`) }))}
            value={viewSettings?.get(SETTINGS_KEYS.GANTT_LINK_DIRECTION) || 'predecessors'}
            withButton
            multiselect={false}
            onSelectItems={([item]) => {
              if (!item) return;
              viewSettingsActions.setOption({
                catalogId,
                viewId,
                viewMode: VIEW_MODES.GANTT,
                option: SETTINGS_KEYS.GANTT_LINK_DIRECTION,
                value: item.key,
              });
            }}
          />
        </Row>
      )}
    </div>
  );
};

export default GanttConfigOverlay;
